import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import './LeftNav.css';
import type { FeatureFlag } from '../../Data/Onboarding/archetypeConfig';
// Onboarding chunk A (§7.9) — subtle Action Center highlight until first visit,
// plus the Getting-started contribution to the Action Center badge.
import { useApiOnboarding } from '../../api/useApiOnboarding';

/* ------------------------------------------------------------------ */
/*  Nav item definition                                                */
/* ------------------------------------------------------------------ */

interface NavItem {
  path: string;
  label: string;
  icon: React.FC;
  /** If true, active match requires exact path (not startsWith). */
  exact?: boolean;
  /** Optional badge count (e.g. pending workflow items). */
  badge?: number;
  /** Optional feature flag gating this item; hidden if the user lacks it. */
  feature?: FeatureFlag;
  /**
   * Optional CLICK target, when it must differ from `path`. `path` stays the
   * ACTIVE-MATCH key (isActive does `startsWith(path)`), so a section item keeps
   * highlighting across all of its sub-routes while still navigating somewhere
   * deeper. Used by My Impact to return the user to the last page they were on
   * inside /memberships instead of always dumping them on the section home.
   * Defaults to `path`.
   */
  navigateTo?: string;
  /**
   * DEV-1458 §7.10 — an EXPLICIT active state that overrides the path-prefix
   * rule. The Home anchor needs this: it is lit only when the user is standing
   * on their resolved default start page, which is a specific community whose
   * URL (guid or slug) the nav cannot derive from `path` alone. App computes the
   * boolean (it owns the resolvers) and hands it down. Undefined ⇒ fall back to
   * the normal path matching.
   */
  activeOverride?: boolean;
}

/** Tier keys used for visibility gating and collapse persistence. */
export type TierKey = 'explore' | 'contribute' | 'lead' | 'govern';

interface NavGroup {
  key: TierKey;
  label: string;
  items: NavItem[];
}

/* ------------------------------------------------------------------ */
/*  localStorage helpers for group expand/collapse                     */
/* ------------------------------------------------------------------ */

const COLLAPSED_GROUPS_KEY = 'l4e_leftnav_collapsed_groups';

function loadCollapsedGroups(): Set<string> {
  try {
    const raw = window?.localStorage?.getItem(COLLAPSED_GROUPS_KEY);
    if (raw) return new Set(JSON.parse(raw));
  } catch { /* ignore */ }
  return new Set();
}

function saveCollapsedGroups(set: Set<string>): void {
  try {
    window?.localStorage?.setItem(COLLAPSED_GROUPS_KEY, JSON.stringify([...set]));
  } catch { /* ignore */ }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

interface LeftNavProps {
  /**
   * Skip 2026-07-13. Where the My Impact item should NAVIGATE — the last path
   * the user visited inside /memberships. The item's active-match key stays
   * '/memberships'. App owns the per-user persistence; defaults to the section
   * home when absent or unremembered.
   */
  myImpactRoute?: string;
  /**
   * DEV-1458 §7.10 — the resolved default start page for the HOME anchor. Same
   * DEV-1448 resolver target the post-login landing uses (one resolver, two
   * consumers). A community canonical URL, or a surface route
   * (/discover, /actions, /memberships, /communities) from an explicit pref.
   * App resolves it; the nav just navigates there.
   */
  homeRoute?: string;
  /**
   * DEV-1458 §7.10 — is the user currently AT their resolved home? Home is an
   * ANCHOR, not a section: it lights only here, never on other communities. App
   * computes this (it owns resolveCommunityFromPath and the resolver), so the
   * comparison survives the guid→slug canonical rewrite that would defeat a
   * naive path match.
   */
  homeActive?: boolean;
  /** Badge count for the Messages nav item (unread + pending requests). */
  messagesBadge?: number;
  /** Action Center badge — ALL unviewed items from the §7.14 shared composer
      (App.tsx composes the same set the inbox renders; badge and list never
      diverge). Replaces the old approvals+onboarding+guidelines triple. */
  actionCenterBadge?: number;
  /** Whether the Action Center nav entry is shown. Hidden for Explorers;
      the /approvals route stays reachable so invitation deep-links still work. */
  showActionCenter?: boolean;
  /** Override-aware unlocked feature set. Items with a `feature` show only if
      present. Omitted = permissive (show all) — e.g. while loading or admins. */
  /**
   * DEV-1482 — the features this user has unlocked. App ALWAYS passes a real Set:
   * every flag for a sysadmin, the server's set once loaded, the per-user cached
   * seed while loading, and an EMPTY set on a cold start. Undefined is treated as
   * empty (fail closed) — never as "show everything", which was the flash bug.
   */
  unlockedFeatures?: Set<FeatureFlag>;
  /**
   * Which tier groups the current user can see.
   * Users "graduate" to tiers via engagement milestones, or are granted
   * specific tiers during account invitation. Groups not in this set
   * are hidden entirely — no dimming, no placeholders.
   * Defaults to ALL tiers visible if omitted (backward-compatible).
   */
  visibleTiers?: Set<TierKey>;
  /**
   * DEV-1019 — Current user GUID, used to read the per-user Default
   * Community View preference. Multiple users on the same browser keep
   * separate preferences so navigation never leaks across accounts.
   */
  currentUserGuid?: string;
  /* DEV-1448's `communitiesLandingRoute` prop REMOVED in DEV-1458 (§7.7 ruling
     5): LeftNav no longer resolves a community target on the My Impact click —
     it navigates to /memberships. The resolver itself survives, unchanged, as
     the "/" post-auth landing's chain (App.tsx). */
}

/** Default: all tiers visible (backward-compatible). */
const ALL_TIERS: Set<TierKey> = new Set(['explore', 'contribute', 'lead', 'govern']);

export default function LeftNav({
  myImpactRoute = '/memberships',
  homeRoute = '/communities',
  homeActive = false,
  messagesBadge = 0,
  actionCenterBadge = 0,
  showActionCenter = true,
  unlockedFeatures,
  visibleTiers = ALL_TIERS,
  currentUserGuid,
}: LeftNavProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useAppTranslation('leftNav');

  // §7.9 — new Explorers get a subtle highlight on the Action Center item
  // until their first visit. The visit writes the server-side
  // action_center_visited flag (never localStorage — §7.9 r4); the shared
  // onboarding cache invalidation clears the highlight without a reload.
  // Null archetype (legacy users) → never highlights.
  const {
    archetype: onboardingArchetype,
    achievedMilestones: onboardingFlags,
    loading: onboardingLoading,
  } = useApiOnboarding(currentUserGuid || null);
  const highlightActionCenter =
    !onboardingLoading &&
    onboardingArchetype === 'explorer' &&
    !onboardingFlags.has('action_center_visited');

  // §7.14 (Track 4 Slice A): the badge count now arrives via the
  // actionCenterBadge prop — App.tsx composes it with the SAME shared
  // composer + seen/resolved store the inbox uses (checklist rows, guidelines
  // Notices, workflows, rhythms, tasks, data entry, and RACI all ride inside
  // that one set). The local onboardingBadge / staleGuidelinesCount /
  // approvalsBadge triple this component used to sum is retired.

  /* ---- Sidebar collapse state (persisted) ---- */
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return window?.localStorage?.getItem('l4e_leftnav_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      window?.localStorage?.setItem('l4e_leftnav_collapsed', String(collapsed));
    } catch {
      // ignore
    }
  }, [collapsed]);

  /* ---- Group expand/collapse state (persisted per-group) ---- */
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(loadCollapsedGroups);

  const expandGroup = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      saveCollapsedGroups(next);
      return next;
    });
  }, []);

  const collapseGroup = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      saveCollapsedGroups(next);
      return next;
    });
  }, []);

  /* ---- Four-tier navigation (Explore / Contribute / Lead / Govern) ---- */
  const allGroups: NavGroup[] = useMemo(
    () => [
      {
        key: 'explore' as TierKey,
        label: t('tierExplore'),
        items: [
          // "My Impact" RELOCATED out of the Explore group to the top of the
          // personal cluster (§7.6 placement ruling, 2026-07-09) — see
          // myImpactItem below. Gating unchanged; tier structure untouched.
          { path: '/discover', label: t('discover'), icon: IconDiscover, feature: 'discover_globe' },
          { path: '/feed', label: t('feed'), icon: IconFeed, feature: 'feed' },
          { path: '/tv', label: t('tv'), icon: IconTv, feature: 'tv' },
        ],
      },
      {
        key: 'contribute' as TierKey,
        label: t('tierContribute'),
        items: [
          { path: '/content', label: t('content'), icon: IconContent },
          { path: '/resources', label: t('resources'), icon: IconResources },
          // Engagements merged into the Action Center (Slice F, DEV-1368). The
          // role list + actions now live there; the cross-cutting "Action Center"
          // nav item is the single entry point. /engagements redirects.
          { path: '/verification', label: t('verification'), icon: IconVerification },
        ],
      },
      {
        key: 'lead' as TierKey,
        label: t('tierLead'),
        items: [
          { path: '/my-projects', label: t('projects'), icon: IconMyProjects, feature: 'project_create' },
          { path: '/problems', label: t('problems'), icon: IconProblems },
          { path: '/solutions', label: t('solutions'), icon: IconSolutions },
          // "Communities" RETIRED in the §7.2 fold (Slice E′, 2026-07-09):
          // community leadership is a role, not a nav destination. The single
          // Explore "Memberships" item + the Roles lens's Leadership chip
          // replace it; /my-communities redirects. The tier keeps
          // Projects / Problems / Solutions.
        ],
      },
      {
        key: 'govern' as TierKey,
        label: t('tierGovern'),
        items: [
          { path: '/portfolio', label: t('portfolio'), icon: IconPortfolio },
          // DEV-1368 — the "Governance" item was a pre-filtered shortcut into
          // the now-universal Action Center (/actions?filter=governance).
          // Governance action items already surface in every user's inbox, so
          // the shortcut added a nav entry without adding any access. Removed
          // (Skip 2026-06-24). Portfolio (the district oversight rollup) stays.
        ],
      },
    ],
    [t]
  );

  /* ---- Filter to only tiers the user has unlocked / been granted ---- */
  const groups = useMemo(
    () =>
      allGroups
        .filter((g) => visibleTiers.has(g.key))
        .map((g) => ({
          ...g,
          items: g.items.filter(
            // DEV-1482 — FAIL CLOSED. This used to read
            //   `!it.feature || !unlockedFeatures || unlockedFeatures.has(...)`
            // so while the onboarding fetch was in flight (set undefined) EVERY
            // gated item rendered, then vanished when the answer arrived — an
            // Explorer flashed the Champion and Govern tiers on every login.
            // App now always passes a real Set (sysadmin = all flags, cold start =
            // empty), so there is no ambiguous "undefined means show everything"
            // branch left to get wrong. An unknown set shows ungated items only.
            (it) => !it.feature || (unlockedFeatures?.has(it.feature) ?? false),
          ),
        }))
        .filter((g) => g.items.length > 0),
    [allGroups, visibleTiers, unlockedFeatures]
  );

  /* ---- Auto-reveal a newly-unlocked tier ----
     When a tier group appears that wasn't visible before (e.g. Contribute the
     moment the user is promoted to Contributor), expand it and pulse it briefly
     so the new section is impossible to miss. Skips the first render so we don't
     override the user's saved collapse preferences on a normal load. DEV-428. */
  const prevGroupKeysRef = useRef<Set<string> | null>(null);
  const [highlightedGroups, setHighlightedGroups] = useState<Set<string>>(new Set());
  useEffect(() => {
    const current = new Set(groups.map((g) => g.key));
    const prev = prevGroupKeysRef.current;
    prevGroupKeysRef.current = current;
    if (prev == null) return; // first render — respect saved collapse state
    const appeared = [...current].filter((k) => !prev.has(k));
    if (appeared.length === 0) return;
    appeared.forEach((k) => expandGroup(k));
    setHighlightedGroups(new Set(appeared));
    const timer = window.setTimeout(() => setHighlightedGroups(new Set()), 4200);
    return () => window.clearTimeout(timer);
  }, [groups, expandGroup]);

  /* ---- Cross-cutting utility items (sit above tier groups) ----
     §7.6 placement ruling (2026-07-09): the PERSONAL CLUSTER order is
     My Impact → Action Center → Messages. My Impact carries the static
     six-point signature mark (never live data — urgency belongs to the
     AC badge); its gating is unchanged (community_browse feature). */
  /* DEV-1458 §7.10 — HOME. A true anchor: it takes the user to their default
     start page (the DEV-1448 resolver target, the same one the post-login
     landing uses — one resolver, two consumers), rendered STANDALONE with no
     rail (§7.7). FIRST in the personal cluster, above My Impact, at every rung —
     an org-invited Explorer's Home correctly resolves to their inviting org.
     The house icon is STATIC, never data-driven (contrast with My Impact's
     six-point mark, which is also static; urgency lives only on the AC badge).
     Active only when AT the resolved home — App supplies that boolean, because
     the home community's URL (guid or slug) can't be matched from a fixed path. */
  const homeItem: NavItem = useMemo(
    () => ({
      path: homeRoute,
      navigateTo: homeRoute,
      label: t('home'),
      icon: IconHome,
      activeOverride: homeActive,
    }),
    [t, homeRoute, homeActive]
  );

  const myImpactItem: NavItem = useMemo(
    () => ({
      /* DEV-1458 (§7.7 ruling 5, Skip 2026-07-12): the nav item points at the
         SECTION, not at a community. It used to point at '/communities' and get
         swapped, at click time, for a resolved community URL — which is how the
         mirror ended up borrowing the world's pages as its main pane. The
         DEV-1448 landing resolver is no longer consulted here; it survives
         unchanged for the "/" post-auth landing (§7.7 ruling 6), which now
         deliberately lands on the community's STANDALONE page. */
      path: '/memberships',
      /* Skip 2026-07-13: return the user to where they LEFT OFF inside the
         section. `path` stays '/memberships' so the item still highlights across
         every /memberships/* route; only the click target moves. This refines
         (does not undo) ruling 5: the click still never RESOLVES a community for
         you — it just doesn't discard where you already were. Falls back to the
         section home when there's nothing remembered. */
      navigateTo: myImpactRoute || '/memberships',
      label: t('memberships'),
      icon: IconMyImpact,
      feature: 'community_browse',
    }),
    [t, myImpactRoute]
  );
  // DEV-1482 — same fail-closed rule as the item filter above. Was
  // `!unlockedFeatures || …`, i.e. shown while loading.
  const showMyImpact = unlockedFeatures?.has('community_browse') ?? false;

  const actionCenterItem: NavItem = useMemo(
    () => ({
      path: '/actions',
      label: t('actionCenter'),
      icon: IconActionCenter,
      // §7.14: ALL unviewed items from the shared composer (App.tsx) — the
      // same set the inbox renders, filtered by the same seen/resolved
      // predicate. Badge and list can never diverge.
      badge: actionCenterBadge || undefined,
    }),
    [t, actionCenterBadge]
  );

  const messagesItem: NavItem = useMemo(
    () => ({ path: '/messages', label: t('messages'), icon: IconMessages, badge: messagesBadge || undefined }),
    [t, messagesBadge]
  );

  /* ---- Active detection ---- */
  const isActive = useCallback(
    (item: NavItem) => {
      // DEV-1458 §7.10 — an explicit active state wins. Home uses it: its lit
      // state is "am I at my resolved home community", which App computes and
      // which no path-prefix rule here can reproduce.
      if (item.activeOverride !== undefined) return item.activeOverride;
      const p = location.pathname;
      const s = location.search;
      /* Strip query params from item.path for pathname matching */
      const [itemPath, itemQuery] = item.path.split('?');
      /* Engagements (Contribute): match /engagements and /engagements/* */
      /* My Projects (Lead): match /my-projects and /my-projects/* */
      if (itemPath === '/my-projects') return p === '/my-projects' || p.startsWith('/my-projects/');
      /* If item has a query param, match both pathname and query */
      if (itemQuery) return p.startsWith(itemPath) && s === `?${itemQuery}`;
      if (item.exact) return p === itemPath;
      return p.startsWith(itemPath);
    },
    [location.pathname, location.search]
  );

  const go = useCallback(
    (path: string) => {
      /* DEV-1458 (§7.7 ruling 5): the '/communities' → resolved-community-URL
         interception is GONE. Every nav item now navigates to its own path;
         My Impact goes to its own home (/memberships), which is what gives the
         section a main pane of its own instead of borrowing a community's.
         (The DEV-1448 resolver is untouched — App.tsx still uses it for the "/"
         post-auth landing. Only this click stopped consulting it.) */
      navigate(path);
    },
    [navigate]
  );

  return (
    <aside className={`l4e-leftnav ${collapsed ? 'collapsed' : ''}`}>
      <div className="l4e-leftnav-top">
        <button
          type="button"
          className="l4e-leftnav-toggle"
          onClick={() => setCollapsed((v) => !v)}
          aria-label={collapsed ? t('expandNavigation') : t('collapseNavigation')}
          title={collapsed ? t('expandNavigation') : t('collapseNavigation')}
        >
          <IconMenu />
        </button>
        {!collapsed && <div className="l4e-leftnav-title">{t('menu')}</div>}
      </div>

      {/* Mini Impact Signature radar RETIRED from the LeftNav (§7.6 /
          DEV-1446, 2026-07-09) — the compact Impact Signature card in the
          My Impact rail is its successor; the full radar lives on the
          signature page. Resolves the external review's "stranded radar". */}

      {/* ── Cross-cutting utility items (Action Center + Messages) ── */}
      <div className="l4e-leftnav-action-center">
        {[
          /* §7.10 — Home is FIRST and shows at EVERY rung (no feature gate):
             everyone has a resolvable home, even a day-zero Explorer. */
          homeItem,
          ...(showMyImpact ? [myImpactItem] : []),
          ...(showActionCenter ? [actionCenterItem] : []),
          messagesItem,
        ].map((item) => {
          const itemActive = isActive(item);
          const ItemIcon = item.icon;
          const nudged = item.path === '/actions' && highlightActionCenter;
          return (
            <button
              key={item.path}
              type="button"
              className={`l4e-leftnav-item ${itemActive ? 'active' : ''} ${nudged ? 'l4e-leftnav-item-nudge' : ''}`}
              /* navigateTo lets an item navigate somewhere other than its
                 active-match key (My Impact returns you to your last in-section
                 page). Defaults to path. */
              onClick={() => go(item.navigateTo ?? item.path)}
              title={item.label}
              aria-current={itemActive ? 'page' : undefined}
            >
              <span className="l4e-leftnav-ico" aria-hidden="true">
                <ItemIcon />
                {item.badge != null && item.badge > 0 && (
                  <span className="l4e-leftnav-dot" />
                )}
              </span>
              {!collapsed && <span className="l4e-leftnav-label">{item.label}</span>}
              {item.badge != null && item.badge > 0 && !collapsed && (
                <span className="l4e-leftnav-badge">{item.badge > 99 ? '99+' : item.badge}</span>
              )}
            </button>
          );
        })}
      </div>

      <nav className="l4e-leftnav-list" aria-label="Primary">
        {groups.map((group, gi) => {
          const groupCollapsed = collapsedGroups.has(group.key);
          const hasActiveChild = group.items.some(isActive);

          return (
            <React.Fragment key={group.key}>
              {/* Group header — clickable toggle when expanded sidebar */}
              {!collapsed && (
                <button
                  type="button"
                  className={`l4e-leftnav-group-label${hasActiveChild ? ' active' : ''}${groupCollapsed ? ' collapsed' : ''}${highlightedGroups.has(group.key) ? ' newly-unlocked' : ''}`}
                  onClick={() => groupCollapsed ? expandGroup(group.key) : collapseGroup(group.key)}
                  aria-expanded={!groupCollapsed}
                  title={groupCollapsed ? t('expandGroup') : t('collapseGroup')}
                >
                  <span className={`l4e-leftnav-group-chevron${groupCollapsed ? ' collapsed' : ''}`}>
                    <IconGroupChevron />
                  </span>
                  <span>{group.label}</span>
                </button>
              )}
              {/* Divider between groups (icon-only mode) */}
              {gi > 0 && collapsed && <div className="l4e-leftnav-divider" />}

              {/* Items — hidden when group is collapsed (except in icon-only sidebar) */}
              {(!groupCollapsed || collapsed) && group.items.map((it) => {
                const active = isActive(it);
                const Icon = it.icon;
                return (
                  <button
                    key={it.path}
                    type="button"
                    className={`l4e-leftnav-item ${active ? 'active' : ''}`}
                    onClick={() => go(it.path)}
                    title={it.label}
                    aria-current={active ? 'page' : undefined}
                  >
                    <span className="l4e-leftnav-ico" aria-hidden="true">
                      <Icon />
                      {it.badge != null && it.badge > 0 && (
                        <span className="l4e-leftnav-dot" />
                      )}
                    </span>
                    {!collapsed && <span className="l4e-leftnav-label">{it.label}</span>}
                    {it.badge != null && it.badge > 0 && !collapsed && (
                      <span className="l4e-leftnav-badge">{it.badge > 99 ? '99+' : it.badge}</span>
                    )}
                  </button>
                );
              })}
            </React.Fragment>
          );
        })}
      </nav>

      <div className="l4e-leftnav-spacer" />

      <div className="l4e-leftnav-bottom">
        {!collapsed && <div className="l4e-leftnav-hint">Life4Earth</div>}
      </div>
    </aside>
  );
}

/* ------------------------------------------------------------------ */
/*  Icons                                                              */
/* ------------------------------------------------------------------ */

function IconMenu() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z" />
    </svg>
  );
}

function IconDiscover() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88" fill="currentColor" />
    </svg>
  );
}

/** Action Center — inbox/tray icon */
function IconActionCenter() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** My Impact — STATIC simplified six-point signature mark (§7.6 placement
 *  ruling, 2026-07-09): a hexagon outline with six vertex points, evoking
 *  the Impact Signature. Never the full radar graphic, never live data. */
/* DEV-1458 §7.10 — the Home anchor's house. Inline SVG to match this file's
   icon convention (IconMyImpact et al. are inline, not lucide imports); the
   handoff's "lucide House" describes the glyph, and the binding rule — STATIC,
   never data-driven — is what an inline path guarantees. */
function IconHome() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M3 11.5 12 4l9 7.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5 10.5V20h5v-5.5h4V20h5v-9.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconMyImpact() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <polygon
        points="12,3 19.8,7.5 19.8,16.5 12,21 4.2,16.5 4.2,7.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="3" r="1.4" fill="currentColor" />
      <circle cx="19.8" cy="7.5" r="1.4" fill="currentColor" />
      <circle cx="19.8" cy="16.5" r="1.4" fill="currentColor" />
      <circle cx="12" cy="21" r="1.4" fill="currentColor" />
      <circle cx="4.2" cy="16.5" r="1.4" fill="currentColor" />
      <circle cx="4.2" cy="7.5" r="1.4" fill="currentColor" />
    </svg>
  );
}

function IconCommunity() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M16 11c1.7 0 3-1.3 3-3s-1.3-3-3-3-3 1.3-3 3 1.3 3 3 3zM8 11c1.7 0 3-1.3 3-3S9.7 5 8 5 5 6.3 5 8s1.3 3 3 3zm0 2c-2.7 0-8 1.3-8 4v2h10v-2c0-1.6.8-2.9 2.1-3.9C10.9 13.0 9.3 13 8 13zm8 0c-.7 0-1.5.1-2.3.3 1.4 1 2.3 2.3 2.3 3.7v2h8v-2c0-2.7-5.3-4-8-4z" />
    </svg>
  );
}

function IconTv() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="2" y="7" width="20" height="15" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
      <polyline points="17 2 12 7 7 2" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function IconMessages() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Data — clipboard/table icon (data obligations and entry) */
function IconData() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="8" y="2" width="8" height="4" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
      <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="8" y1="16" x2="13" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/** Resources — package/box icon (resource registries and matching) */
function IconResources() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="12" y1="22.08" x2="12" y2="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** My Projects — folder with star (owned/managed) */
function IconMyProjects() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <polygon points="12 11 13.1 14.5 16.5 14.5 13.7 16.7 14.8 20 12 17.8 9.2 20 10.3 16.7 7.5 14.5 10.9 14.5" fill="currentColor" />
    </svg>
  );
}

/** Content — layered pages icon (unified content: chronicles + knowledge) */
function IconContent() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="9" y1="7" x2="15" y2="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="9" y1="11" x2="15" y2="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/** @deprecated Use IconContent. Kept for backward compatibility during migration. */
function IconChronicles() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points="14 2 14 8 20 8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="9" y1="13" x2="15" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="9" y1="17" x2="13" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/** @deprecated Use IconContent. Kept for backward compatibility during migration. */
function IconKnowledge() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Portfolio — grid/dashboard icon */
function IconPortfolio() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
      <rect x="14" y="3" width="7" height="4" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
      <rect x="14" y="10" width="7" height="11" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
      <rect x="3" y="13" width="7" height="8" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

/** Problems — alert-triangle icon (issues and challenges) */
function IconProblems() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="12" y1="9" x2="12" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="12" y1="17" x2="12.01" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/** Solutions — lightbulb icon (ideas and approaches) */
function IconSolutions() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 18h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 22h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Feed — RSS/activity feed icon */
function IconFeed() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 11a9 9 0 0 1 9 9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M4 4a16 16 0 0 1 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="5" cy="19" r="1.5" fill="currentColor" />
    </svg>
  );
}

/** Verification — shield-check icon (trust and validation) */
function IconVerification() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 12l2 2 4-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Small chevron-down — group expand/collapse indicator */
function IconGroupChevron() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true">
      <polyline points="6 9 12 15 18 9" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
